import React from 'react';
import useSessionInfo from '../../../../hooks/useSessionInfo';
import { Flex } from 'rebass';
import { SearchInput } from '../../../../components/Form';
import PropTypes from 'prop-types';
import { isBrowser } from 'react-device-detect';
import { FilterBarProps } from '../../../../components/FilterBar/FilterBar';
import { rebassProps } from '../../../../theme/theme';
import ResetFiltersButton from '../../../../components/FilterBar/ResetFiltersButton';
import {
  PropertyType,
  PropertyTypePicker,
} from '../../../../components/WaterService/PropertyTypePicker';
import { GenericStatusPicker } from '../../../../components/GenericStatusPicker';
import { getStringSearchPlaceholder } from '../../../../helpers/search';
import { searchFields } from './WaterServices';
import TagPicker from '../../../../components/TagPicker/TagPicker';
import {
  HazardProtectionStatus,
  HazardProtectionStatusPicker,
} from '../../../../components/WaterService/HazardProtectionStatusPicker';
import ReactSelect from '../../../../components/ReactSelect';

export type WaterServiceFilters = {
  status?: 'ACTIVE' | 'INACTIVE';
  type?: PropertyType[];
  hazardProtectionStatus?: HazardProtectionStatus[];
  hasBackflowAssemblies?: boolean;
  stringSearch?: string | undefined;
  tags?: string[];
};

export const defaultFilters: WaterServiceFilters = {
  status: 'ACTIVE',
  type: undefined,
  hazardProtectionStatus: undefined,
  hasBackflowAssemblies: undefined,
  stringSearch: undefined,
  tags: undefined,
};

const hasAssembliesOptions = [
  { value: true, label: 'Has Assemblies' },
  { value: false, label: 'No Assemblies' },
] as const;

type FilterBarWaterServicesProps = FilterBarProps<WaterServiceFilters>;

const FilterBarWaterServices: React.FC<FilterBarWaterServicesProps> = (
  props
) => {
  const { filters, setFilters, selectedId } = props;
  const { features } = useSessionInfo();
  const hasWaterHazards = features.includes('WATER_HAZARDS');

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFilters((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <Flex variant="filterBar.container" {...(props as rebassProps)}>
      <Flex variant="filterBar.left">
        <SearchInput
          placeholder={getStringSearchPlaceholder(searchFields)}
          autoFocus={isBrowser && !selectedId}
          width={350}
          name="stringSearch"
          value={filters.stringSearch === undefined ? '' : filters.stringSearch}
          onChange={onChange}
        />
        <GenericStatusPicker
          value={filters.status}
          isClearable
          onChange={(value) => {
            setFilters({
              ...filters,
              status: value,
            });
          }}
        />
        <PropertyTypePicker
          isMulti={true}
          onChange={(value) => {
            setFilters({
              ...filters,
              type: value,
            });
          }}
          value={filters.type}
        />
        {hasWaterHazards && (
          <HazardProtectionStatusPicker
            isMulti={true}
            onChange={(value) => {
              setFilters({
                ...filters,
                hazardProtectionStatus: value as
                  | HazardProtectionStatus[]
                  | undefined,
              });
            }}
            value={filters.hazardProtectionStatus}
          />
        )}
        <ReactSelect
          placeholder="assemblies"
          isClearable
          value={
            filters.hasBackflowAssemblies === undefined
              ? null
              : (hasAssembliesOptions.find(
                  (o) => o.value === filters.hasBackflowAssemblies
                ) ?? null)
          }
          onChange={(selected: any) => {
            setFilters({
              ...filters,
              hasBackflowAssemblies:
                selected != null ? selected.value : undefined,
            });
          }}
          options={hasAssembliesOptions}
        />
        <TagPicker
          value={filters.tags}
          placeholder="Tags"
          isCreatable={false}
          usedOn="WATER_SERVICE"
          onChange={(newTags) =>
            setFilters((prev) => ({
              ...prev,
              tags: newTags.length > 0 ? newTags.slice() : undefined,
            }))
          }
        />
      </Flex>
      <Flex variant="filterBar.right">
        <ResetFiltersButton setFilters={setFilters} />
      </Flex>
    </Flex>
  );
};

FilterBarWaterServices.propTypes = {
  filters: PropTypes.any,
  setFilters: PropTypes.func.isRequired,
};

export default FilterBarWaterServices;
